import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Award,
  BookOpen,
  ChevronDown,
  Clock,
  ClipboardList,
  Clock3,
  Filter,
  Layers,
  LayoutGrid,
  List,
  Rows3,
  Search,
  Settings2,
  Star,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { categoriesService, coursesService } from '../services/api';
import { ContentCategoryPublic, Course } from '../types';
import { haystackIncludesFa, htmlToPlainSnippet, normalizeFaSearch } from '../utils/faSearchNormalize';
import { getImageUrl, getImageUrlWithFallback } from '../utils/imageUtils';
import { getCoursePathSegment } from '../utils/coursePath';
import { FreeBadge } from '../components/FreeBadge';

type SortKey = 'newest' | 'popular' | 'rating' | 'price-asc' | 'price-desc';

const SORT_BAR: { key: SortKey; label: string }[] = [
  { key: 'newest', label: 'پیش‌فرض · جدیدترین' },
  { key: 'popular', label: 'محبوب‌ترین' },
  { key: 'rating', label: 'بیشترین امتیاز' },
  { key: 'price-asc', label: 'ارزان‌ترین' },
  { key: 'price-desc', label: 'گران‌ترین' },
];

/** سطل زمانی مجموع مدت ویدئوهای منتشر‌شدهٔ دوره */
type DurationBucket = '' | 'h01' | 'h15' | 'h510' | 'h10plus';

const DURATION_FILTERS: { id: DurationBucket; label: string }[] = [
  { id: '', label: 'هر طولی' },
  { id: 'h01', label: 'تا حدود ۱ ساعت' },
  { id: 'h15', label: '۱ تا ۵ ساعت' },
  { id: 'h510', label: '۵ تا ۱۰ ساعت' },
  { id: 'h10plus', label: 'بیش از ۱۰ ساعت' },
];

const BRAND_BLUE = '#1C3B9B';
const BRAND_PINK = '#C11277';

/** همان سرمه‌ای دکمهٔ «افزودن به سبد» در قالب دوره (CourseDetail) */
const COURSE_CTA_NAVY = '#3A68C2';

/** زمینهٔ کمی متمایز برای پنل فیلتر و بدنهٔ صفحه */
const BROWSE_SURFACE = '#f4f7fc';

/** محتوای Rich Text دسته؛ خالی/فقط `<p><br></p>` را نادیده بگیر */
function categoryRichHtmlIsNonEmpty(html: string | null | undefined): boolean {
  if (!html?.trim()) return false;
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > 0;
}

/** Rich Text دسته؛ هم‌کلاس دورهٔ CourseDetail برای پیش‌نمایش نزدیک به سایت */
const BROWSE_CATEGORY_HTML_PROSE_CLASS =
  'browse-category-html prose prose-sm max-w-none min-w-0 overflow-x-auto overflow-y-visible text-[#334155] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:prose-base prose-p:leading-7 prose-headings:text-[#0f172a] prose-li:my-1 prose-img:max-w-full prose-img:rounded-xl prose-img:shadow-sm prose-a:text-[#3A68C2] prose-blockquote:border-s-[#3A68C2]/35 prose-strong:text-[#0f172a]';

type BrowseCategoryRichCollapsibleProps = {
  html: string;
  /** با تغییر دسته/محتوا، حالت باز بسته می‌شود و ارتفاع دوباره سنجیده می‌شود */
  segmentKey: string;
  loading?: boolean;
  /** داخل قاب بیرونی هم‌تم؛ بدون حلقه و گوشهٔ اضافه */
  embed?: boolean;
};

function BrowseCategoryRichCollapsible({
  html,
  segmentKey,
  loading = false,
  embed = false,
}: BrowseCategoryRichCollapsibleProps) {
  const [expanded, setExpanded] = useState(false);
  const [needsToggle, setNeedsToggle] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpanded(false);
  }, [segmentKey]);

  useLayoutEffect(() => {
    if (!html?.trim()) {
      setNeedsToggle(false);
      return;
    }

    const el = contentRef.current;
    if (!el) return;
    const maxCollapsedPx =
      typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches ? 176 : 144;

    requestAnimationFrame(() => {
      const prose = el.querySelector('.browse-category-html') as HTMLElement | null;
      const target = prose ?? (el.querySelector('.browse-category-html-wrap') as HTMLElement | null);
      if (!target) return;
      setNeedsToggle(target.scrollHeight > maxCollapsedPx + 12);
    });
  }, [html, segmentKey, expanded, loading]);

  const frameClass = embed
    ? 'relative overflow-hidden'
    : 'relative overflow-hidden rounded-xl px-3 py-3 ring-1 ring-slate-200/40 sm:px-5 sm:py-4';

  return (
    <div ref={contentRef} className={frameClass} style={{ backgroundColor: BROWSE_SURFACE }}>
      <div
        className={`browse-category-html-wrap ${
          expanded || !needsToggle ? '' : 'max-h-[9rem] overflow-hidden sm:max-h-[11rem]'
        }`}
      >
        <div
          className={BROWSE_CATEGORY_HTML_PROSE_CLASS}
          dir="rtl"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
      {!expanded && needsToggle ? (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-10 z-[1] h-14 sm:bottom-11"
          style={{
            background: `linear-gradient(to top, ${BROWSE_SURFACE}, transparent)`,
          }}
          aria-hidden
        />
      ) : null}
      {needsToggle ? (
        <div className="relative z-[2] mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#3A68C2]/35 bg-[#3A68C2]/12 px-4 py-2 text-xs font-bold text-[#2855a3] shadow-sm transition hover:bg-[#3A68C2]/18"
          >
            {expanded ? 'نمایش کمتر' : 'مشاهده بیشتر'}
            <ChevronDown
              className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function payableTomans(course: Course): number {
  const v = course.effectivePrice ?? course.price;
  return typeof v === 'number' ? v : Number(v) || 0;
}

function listTomans(course: Course): number {
  const v = course.listPrice ?? course.price;
  return typeof v === 'number' ? v : Number(v) || 0;
}

const palette = ['#1C3B9B', '#C11277', '#6366f1', '#0891b2', '#059669', '#d97706'];

function accentForSlug(slug: string, color?: string | null): string {
  if (color?.match(/^#[0-9a-fA-F]{3,8}$/)) return color;
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h + slug.charCodeAt(i) * (i + 1)) % palette.length;
  return palette[h];
}

/** هش پایدار روی شناسه دوره (برای مهلت/٪ پیشنهادی نمایشی) */
function courseIdHash(courseId: string): number {
  let h = 0;
  for (let i = 0; i < courseId.length; i++) h = (Math.imul(31, h) + courseId.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * زمان پایان شمارنده — ترجیحاً از سرور؛ وگرنه مهلت پیشنهاد پایدار (ریست روزانه) تا عدد هر ثانیه نپرد.
 */
function stableOfferEndsAt(course: Course): Date | null {
  const pay = payableTomans(course);
  if (pay <= 0) return null;
  if (course.discountEndsAt) {
    const d = new Date(course.discountEndsAt);
    if (Number.isFinite(d.getTime()) && d.getTime() > Date.now()) return d;
  }
  const epochDay = Math.floor(Date.now() / 86400000);
  const h = courseIdHash(`${course.id}:${epochDay}`);
  const horizonSec = (18 + (h % 54)) * 3600 + (h % 3600);
  let tMs = epochDay * 86400000 + horizonSec * 1000;
  while (tMs <= Date.now()) tMs += 6 * 3600 * 1000;
  if (tMs - Date.now() > 172 * 3600 * 1000) tMs = Date.now() + ((h % 120) + 24) * 3600 * 1000;
  return new Date(tMs);
}

/** ارقام فارسی برای شمارندهٔ مینیمال */
function toFaDigits(n: number, size = 2): string {
  const s = String(Math.max(0, Math.floor(n))).padStart(size, '0');
  return s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
}

function MinimalOfferCountdown({ endsAt }: { endsAt: Date }) {
  const [remainMs, setRemainMs] = useState(() => Math.max(0, endsAt.getTime() - Date.now()));

  useEffect(() => {
    const tick = () => setRemainMs(Math.max(0, endsAt.getTime() - Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [endsAt]);

  if (remainMs <= 0) {
    return <span className="tabular-nums text-[11px] font-semibold text-red-400/90 sm:text-[12px]">به‌روزرسانی …</span>;
  }

  const s = Math.floor(remainMs / 1000);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  let label = '';
  if (hh >= 72) label = `${toFaDigits(Math.floor(hh / 24), 1)}روز`;
  else if (hh > 0) label = `${toFaDigits(hh)}:${toFaDigits(mm)}:${toFaDigits(ss)}`;
  else label = `${toFaDigits(mm)}:${toFaDigits(ss)}`;

  return (
    <span className="inline-flex tabular-nums text-[11px] font-extrabold tracking-tight text-red-600 sm:text-[12px]">
      {label}
    </span>
  );
}

function carouselDiscountPercent(course: Course): number {
  if (!course.hasActiveDiscount) return 0;
  if (course.discountType === 'PERCENT' && course.discountPercent != null) {
    const p = Math.round(Number(course.discountPercent));
    if (Number.isFinite(p) && p > 0 && p <= 100) return p;
  }
  const pay = payableTomans(course);
  const list = listTomans(course);
  if (list > pay && pay >= 0 && list > 0) {
    const implied = Math.round((1 - pay / list) * 100);
    if (implied > 0 && implied <= 100) return implied;
  }
  return 0;
}

/** قیمت / ٪ پیشنهادی نمایشی کارت (پیشنهاد برای هر دورهٔ پولی وقتی API خاموش است) */
function browseCardOffer(course: Course): {
  pct: number;
  listShown: number;
  payShown: number;
  endsAt: Date | null;
} {
  const pay = payableTomans(course);
  const listRaw = listTomans(course);
  const endsAt = stableOfferEndsAt(course);

  if (pay <= 0) return { pct: 0, listShown: listRaw, payShown: pay, endsAt: null };

  let pct = carouselDiscountPercent(course);
  const listOk = Number.isFinite(listRaw) && listRaw > pay;

  if (pct > 0 && listOk) return { pct, listShown: listRaw, payShown: pay, endsAt };
  if (listOk && pct === 0) {
    pct = Math.min(99, Math.max(1, Math.round((1 - pay / listRaw) * 100)));
    return { pct, listShown: listRaw, payShown: pay, endsAt };
  }

  const h = courseIdHash(course.id);

  if (pct > 0 && !listOk) {
    const listSynth = Math.max(
      Math.round((pay * 100) / Math.max(100 - pct, 1)),
      pay + 8000 + (h % 11) * 1000,
    );
    return { pct, listShown: listSynth, payShown: pay, endsAt };
  }

  pct = 8 + (h % 13);
  const listDerived = Math.max(
    Math.round((pay * 100) / Math.max(100 - pct, 1)),
    pay + 12000 + (h % 9) * 1500,
  );
  return { pct, listShown: listDerived, payShown: pay, endsAt };
}

function carouselLessonCount(course: Course): number {
  if (course.chapters?.length) {
    return course.chapters.reduce((n, ch) => n + (ch.lessons?.length ?? 0), 0);
  }
  return (course.videos?.length ?? 0) + (course.audios?.length ?? 0);
}

function carouselRatingLine(course: Course): string {
  const s = course.satisfactionAvgLabel?.trim();
  if (s) return s;
  return '۴٫۸';
}

function carouselInstructorLine(course: Course): string {
  return (course.teachingFormat?.trim() || course.courseLevel?.trim() || 'اساتید موسسه').slice(0, 42);
}

function carouselDurationLine(course: Course): string {
  return (course.suggestedDailyTime?.trim() || 'خودآموز').slice(0, 28);
}

/** تبدیل ارقام فارسی/عربی به لاتین برای پارس عدد امتیاز */
function normalizeDigits(input: string): string {
  const fa = '۰۱۲۳۴۵۶۷۸۹';
  const ar = '٠١٢٣٤٥٦٧٨٩';
  let s = '';
  for (const ch of input) {
    const fi = fa.indexOf(ch);
    if (fi >= 0) {
      s += String(fi);
      continue;
    }
    const ai = ar.indexOf(ch);
    if (ai >= 0) {
      s += String(ai);
      continue;
    }
    if (ch === '٫' || ch === '،') {
      s += '.';
      continue;
    }
    s += ch;
  }
  return s;
}

/** امتیاز تخمینی از برچسب رضایت؛ اگر پارس نشود null */
function parsedCourseRating(course: Course): number | null {
  const raw = course.satisfactionAvgLabel?.trim();
  if (!raw) return null;
  const simplified = normalizeDigits(raw);
  const m = simplified.match(/(\d+\.?\d*)/);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (!Number.isFinite(v) || v < 0 || v > 5) return null;
  return v;
}

function totalPublishedMediaSeconds(course: Course): number {
  let sec = 0;
  const vids = course.videos ?? [];
  for (const v of vids) sec += v.duration ?? 0;
  const auds = course.audios ?? [];
  for (const a of auds) sec += a.duration ?? 0;
  return sec;
}

function matchesDurationBucket(course: Course, bucket: DurationBucket): boolean {
  if (!bucket) return true;
  const t = totalPublishedMediaSeconds(course);
  if (t <= 0) return false;
  const h3600 = 3600;
  const h5 = h3600 * 5;
  const h10 = h3600 * 10;
  switch (bucket) {
    case 'h01':
      return t <= h3600;
    case 'h15':
      return t > h3600 && t <= h5;
    case 'h510':
      return t > h5 && t <= h10;
    case 'h10plus':
      return t > h10;
    default:
      return true;
  }
}

/** آمار نمایشی بنر کتابخانه / دسته‌ها */
function summarizeBrowseAudience(list: Course[]): {
  enrollSum: number;
  lessonSum: number;
  mediaHours: number;
} {
  let enrollSum = 0;
  let lessonSum = 0;
  let mediaSec = 0;
  for (const c of list) {
    enrollSum += c._count?.enrollments ?? 0;
    lessonSum += carouselLessonCount(c);
    mediaSec += totalPublishedMediaSeconds(c);
  }
  return {
    enrollSum,
    lessonSum,
    mediaHours: Math.round((mediaSec / 3600) * 100) / 100,
  };
}

const RATING_CHOICES: { value: string; label: string }[] = [
  { value: '', label: 'همهٔ امتیازها' },
  { value: '3.5', label: '۳٫۵ و بالاتر' },
  { value: '4', label: '۴ و بالاتر' },
  { value: '4.5', label: '۴٫۵ و بالاتر' },
];

function Accordion({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl px-1 py-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-1 py-2 text-start text-[13px] font-semibold tracking-tight text-slate-700 transition-colors hover:bg-slate-50/60 hover:text-slate-900 sm:text-[13.5px]"
      >
        <span>{title}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#3A68C2]/45 transition-transform duration-200 ${open ? '-rotate-180' : ''}`}
        />
      </button>
      {open ? <div className="border-t border-slate-100 pb-2 pt-3">{children}</div> : null}
    </div>
  );
}

/** جستجوی دسته — همان نرمال‌سازی فارسی/عربی هدر و لیست دوره‌ها */
function topicTextMatches(text: string, rawQuery: string): boolean {
  if (!normalizeFaSearch(rawQuery)) return true;
  return haystackIncludesFa(text, rawQuery);
}

function FilterPanelContent({
  categories,
  courses,
  categoryCounts,
  uncategorizedCount,
  selectedSlug,
  setCategorySlug,
  searchTerm,
  setSearchTerm,
  onlyDiscount,
  setOnlyDiscount,
  onlyIntro,
  setOnlyIntro,
  onlyFree,
  setOnlyFree,
  resetFilters,
  activeFilterCount,
  ratingMin,
  setRatingMin,
  durationBucket,
  setDurationBucket,
  levelFilter,
  setLevelFilter,
  typeFilter,
  setTypeFilter,
  levelOptions,
  typeOptions,
  topicCategorySearch,
  setTopicCategorySearch,
}: {
  categories: ContentCategoryPublic[];
  courses: Course[];
  categoryCounts: Map<string, number>;
  uncategorizedCount: number;
  selectedSlug: string;
  setCategorySlug: (s: string) => void;
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  onlyDiscount: boolean;
  setOnlyDiscount: (v: boolean) => void;
  onlyIntro: boolean;
  setOnlyIntro: (v: boolean) => void;
  onlyFree: boolean;
  setOnlyFree: (v: boolean) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  ratingMin: string;
  setRatingMin: (s: string) => void;
  durationBucket: DurationBucket;
  setDurationBucket: (b: DurationBucket) => void;
  levelFilter: string;
  setLevelFilter: (s: string) => void;
  typeFilter: string;
  setTypeFilter: (s: string) => void;
  levelOptions: string[];
  typeOptions: string[];
  topicCategorySearch: string;
  setTopicCategorySearch: (value: string) => void;
}) {
  const topicQueryTrimmed = topicCategorySearch.trim();
  const filteredCategories = useMemo(
    () =>
      topicQueryTrimmed
        ? categories.filter((cat) => topicTextMatches(cat.name, topicQueryTrimmed))
        : categories,
    [categories, topicQueryTrimmed],
  );

  const showUncategorizedTopicRow =
    uncategorizedCount > 0 &&
    (!topicQueryTrimmed || topicTextMatches('بدون دسته', topicQueryTrimmed));

  return (
    <div className="flex flex-col gap-5">
      <div>
        <label htmlFor="browse-course-search" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500">
          جستجو
        </label>
        <div className="group relative rounded-2xl bg-white/90 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_28px_-14px_rgba(28,59,155,0.12)] ring-1 ring-slate-200/50 transition-[box-shadow,ring] duration-200 focus-within:bg-white focus-within:shadow-[0_4px_20px_-8px_rgba(58,104,194,0.22)] focus-within:ring-2 focus-within:ring-[#3A68C2]/28">
          <input
            id="browse-course-search"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="عنوان دوره یا موضوع…"
            dir="rtl"
            autoComplete="off"
            aria-label="جستجوی دوره"
            className="w-full rounded-2xl border-0 bg-transparent py-3 ps-4 pe-12 text-[13px] text-slate-800 outline-none ring-0 placeholder:text-slate-400"
          />
          <span
            className="pointer-events-none absolute end-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl bg-[#3A68C2]/[0.09] text-[#3A68C2]/80"
            aria-hidden
          >
            <Search className="h-4 w-4" strokeWidth={2.25} />
          </span>
        </div>
      </div>

      <Accordion title="امتیاز دوره">
        <div className="space-y-1">
          {RATING_CHOICES.map(({ value, label }) => (
            <label
              key={value || 'all'}
              className="flex cursor-pointer items-start gap-2 rounded-lg px-1.5 py-1.5 text-[12px] transition-colors hover:bg-slate-50 sm:text-[13px]"
            >
              <input
                type="radio"
                name="rating-min"
                checked={ratingMin === value}
                onChange={() => setRatingMin(value)}
                className="mt-0.5 h-3.5 w-3.5 border-slate-300 text-[#3A68C2] focus:ring-[#3A68C2]/30"
              />
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1 font-semibold text-[#334155]">
                {value ? (
                  <>
                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
                    <span>{label}</span>
                  </>
                ) : (
                  label
                )}
              </span>
            </label>
          ))}
        </div>
      </Accordion>

      <Accordion title="مدت تقریبی محتوا (ویدئو و صوت)">
        <div className="space-y-1">
          {DURATION_FILTERS.map(({ id, label }) => (
            <label
              key={id || 'all-dur'}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1.5 text-[12px] transition-colors hover:bg-slate-50 sm:text-[13px]"
            >
              <input
                type="radio"
                name="duration-bucket"
                checked={durationBucket === id}
                onChange={() => setDurationBucket(id)}
                className="h-3.5 w-3.5 border-slate-300 text-[#3A68C2] focus:ring-[#3A68C2]/30"
              />
              <span className="font-semibold text-[#334155]">{label}</span>
            </label>
          ))}
        </div>
      </Accordion>

      <Accordion title="موضوع و دسته">
        <div className="mb-3">
          <label htmlFor="browse-topic-search" className="mb-1.5 block text-[10px] font-semibold text-slate-500">
            جستجو بین دسته‌ها
          </label>
          <div className="relative rounded-xl border border-slate-200/95 bg-white shadow-sm ring-1 ring-slate-200/35 transition-[box-shadow,ring] focus-within:ring-2 focus-within:ring-[#3A68C2]/22">
            <input
              id="browse-topic-search"
              type="search"
              value={topicCategorySearch}
              onChange={(e) => setTopicCategorySearch(e.target.value)}
              placeholder="نام دسته…"
              dir="rtl"
              autoComplete="off"
              aria-label="جستجو در بین دسته‌ها"
              className="w-full rounded-xl border-0 bg-transparent py-2 ps-3 pe-10 text-[12px] text-slate-800 outline-none ring-0 placeholder:text-slate-400"
            />
            <Search
              className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              strokeWidth={2.25}
              aria-hidden
            />
          </div>
        </div>
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => setCategorySlug('')}
            className={`flex w-full items-center justify-between rounded-md px-1.5 py-1.5 text-start text-[12px] font-semibold sm:text-[13px] ${
              !selectedSlug ? 'bg-[#3A68C2]/12 text-[#2d5299]' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            همهٔ دوره‌ها
            <span className="tabular-nums text-xs opacity-70">{courses.length}</span>
          </button>
          {filteredCategories.map((cat) => {
            const active = selectedSlug === cat.slug;
            const dot = accentForSlug(cat.slug, cat.color);
            const count = categoryCounts.get(cat.slug) ?? 0;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategorySlug(active ? '' : cat.slug)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-1.5 py-1.5 text-start text-[12px] font-semibold sm:text-[13px] ${
                  active ? 'bg-[#3A68C2]/12 text-[#2d5299]' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: dot }} />
                  <span className="truncate">{cat.name}</span>
                </span>
                <span className="tabular-nums text-xs text-slate-500">{count}</span>
              </button>
            );
          })}
          {showUncategorizedTopicRow ? (
            <button
              type="button"
              onClick={() => setCategorySlug(selectedSlug === '_none' ? '' : '_none')}
              className={`flex w-full items-center justify-between rounded-md px-1.5 py-1.5 text-[12px] font-semibold sm:text-[13px] ${
                selectedSlug === '_none' ? 'bg-amber-50 text-amber-900' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              بدون دسته
              <span className="text-xs">{uncategorizedCount}</span>
            </button>
          ) : null}
        </div>
        {topicQueryTrimmed && filteredCategories.length === 0 ? (
          <p className="pt-3 text-center text-[11px] leading-relaxed text-slate-500">دسته‌ای با این عبارت نیست.</p>
        ) : null}
      </Accordion>

      <Accordion title="سطح دوره">
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => setLevelFilter('')}
            className={`w-full rounded-md px-1.5 py-1.5 text-start text-[12px] font-semibold sm:text-[13px] ${!levelFilter ? 'bg-[#3A68C2]/12 text-[#2d5299]' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            هر سطحی
          </button>
          {levelOptions.map((lv) => (
            <button
              key={lv}
              type="button"
              onClick={() => setLevelFilter(levelFilter === lv ? '' : lv)}
              className={`w-full truncate rounded-md px-1.5 py-1.5 text-start text-[12px] font-semibold sm:text-[13px] ${levelFilter === lv ? 'bg-[#3A68C2]/12 text-[#2d5299]' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {lv}
            </button>
          ))}
        </div>
      </Accordion>

      <Accordion title="نوع آموزش">
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => setTypeFilter('')}
            className={`w-full rounded-md px-1.5 py-1.5 text-start text-[12px] font-semibold sm:text-[13px] ${!typeFilter ? 'bg-[#3A68C2]/12 text-[#2d5299]' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            همهٔ انواع
          </button>
          {typeOptions.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTypeFilter(typeFilter === t ? '' : t)}
              className={`w-full truncate rounded-md px-1.5 py-1.5 text-start text-[12px] font-semibold sm:text-[13px] ${typeFilter === t ? 'bg-[#3A68C2]/12 text-[#2d5299]' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </Accordion>

      <Accordion title="پیشنهاد ویژه" defaultOpen={false}>
        <div className="space-y-2">
          {[
            { on: onlyDiscount, set: setOnlyDiscount, label: 'فقط تخفیف‌دار فعال' },
            { on: onlyIntro, set: setOnlyIntro, label: 'دارای ویدئو معرفی' },
            { on: onlyFree, set: setOnlyFree, label: 'رایگان' },
          ].map((t) => (
            <label
              key={t.label}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-1 py-2 transition-colors hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={t.on}
                onChange={(e) => t.set(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-[#3A68C2] focus:ring-[#3A68C2]/25"
              />
              <span className="text-[12px] font-semibold text-[#475569] sm:text-[13px]">{t.label}</span>
            </label>
          ))}
        </div>
      </Accordion>

      {activeFilterCount > 0 ? (
        <button
          type="button"
          onClick={resetFilters}
          className="w-full rounded-full bg-rose-50/95 py-3 text-[12px] font-semibold text-rose-700 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.18)] transition hover:bg-rose-100/90 hover:text-rose-800 sm:text-[13px]"
        >
          پاک کردن فیلترها ({activeFilterCount.toLocaleString('fa-IR')})
        </button>
      ) : null}
    </div>
  );
}

function ToolbarSortPills({
  sortKey,
  setSortKey,
}: {
  sortKey: SortKey;
  setSortKey: (k: SortKey) => void;
}) {
  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      {SORT_BAR.map(({ key, label }) => {
        const on = sortKey === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setSortKey(key)}
            className={`relative shrink-0 whitespace-nowrap rounded-md border px-3 py-1.5 text-[11px] font-bold transition sm:text-[12px] sm:px-3.5 sm:py-2 ${
              on
                ? 'border-[#3A68C2] bg-[#3A68C2] text-white shadow-sm hover:bg-[#345DAF]'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#0f172a]'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function CourseCardGrid({
  course,
  index,
  randomFallback,
  navigate,
}: {
  course: Course;
  index: number;
  randomFallback: string;
  navigate: (path: string) => void;
}) {
  const thumb = course.thumbnail ? getImageUrlWithFallback(course.thumbnail, randomFallback) : randomFallback;
  const lessons = carouselLessonCount(course);
  const offer = browseCardOffer(course);
  const discountPct = offer.pct;
  const showPromoStripe = !!offer.endsAt && offer.payShown > 0 && discountPct > 0;
  const showOldPrice =
    Number.isFinite(offer.listShown) && offer.payShown > 0 && offer.listShown > offer.payShown;

  return (
    <li className="flex h-full w-full justify-stretch">
      <motion.button
        type="button"
        onClick={() => navigate(`/courses/${getCoursePathSegment(course)}`)}
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.12 }}
        transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.28) }}
        whileHover={{ y: -4 }}
        className="group relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-slate-200/95 bg-white text-right shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.1)] ring-0 transition-[box-shadow,border-color] duration-300 hover:border-slate-300 hover:shadow-[0_4px_6px_-1px_rgba(15,23,42,0.06),0_20px_36px_-18px_rgba(15,23,42,0.16)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A68C2]/35 focus-visible:ring-offset-2"
      >
        <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-slate-100">
          <img
            src={thumb}
            alt={course.title}
            className="absolute inset-0 h-full w-full object-cover transition duration-500 ease-out group-hover:scale-[1.04]"
            loading={index < 8 ? 'eager' : 'lazy'}
            onError={(e) => {
              (e.target as HTMLImageElement).src = randomFallback;
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent opacity-90" />
          {discountPct > 0 ? (
            <span className="absolute end-2.5 top-2.5 z-20 inline-flex items-center rounded-md bg-[#dc2626] px-2 py-1 text-[10px] font-extrabold text-white shadow-[0_4px_14px_-2px_rgba(220,38,38,0.45)] ring-1 ring-white/25">
              ٪{discountPct.toLocaleString('fa-IR')}
            </span>
          ) : null}
        </div>

        <div className="flex min-h-[11.5rem] flex-1 flex-col gap-2.5 p-3 pt-3 sm:min-h-[12rem] sm:gap-3 sm:p-3.5">
          <h3 className="line-clamp-2 min-h-[2.5rem] text-right text-[13px] font-bold leading-snug tracking-tight text-slate-900 transition-colors group-hover:text-[#2d5299] sm:min-h-[2.75rem] sm:text-[0.9375rem]">
            {course.title}
          </h3>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-slate-500 sm:text-[11px]">
            <span className="inline-flex min-w-0 items-center gap-1 font-semibold text-amber-600">
              <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" aria-hidden />
              <span className="truncate">{carouselRatingLine(course)}</span>
            </span>
            <span className="hidden h-3 w-px bg-slate-200 sm:inline" aria-hidden />
            <span className="inline-flex min-w-0 items-center gap-1">
              <Clock3 className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
              <span className="truncate">{carouselDurationLine(course)}</span>
            </span>
            <span className="hidden h-3 w-px bg-slate-200 sm:inline" aria-hidden />
            <span className="font-medium tabular-nums text-slate-600">
              {lessons.toLocaleString('fa-IR')} جلسه
            </span>
          </div>

          <p className="line-clamp-1 min-h-[1.125rem] text-[10px] text-slate-500 sm:text-[11px]">
            <UserRound className="mb-0.5 me-1 inline h-3 w-3 text-slate-400 align-text-bottom" aria-hidden />
            {carouselInstructorLine(course)}
          </p>

          <div className="mt-auto">
            {showPromoStripe && offer.endsAt ? (
              <div className="-mx-3 mb-2.5 sm:-mx-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-3.5">
                  <span className="shrink-0 text-[10px] font-semibold text-red-600 sm:text-[11px]">پیشنهاد</span>
                  <span className="inline-flex shrink-0">
                    <MinimalOfferCountdown endsAt={offer.endsAt} />
                  </span>
                </div>
                <div className="mt-2 border-b border-red-200/90" aria-hidden />
              </div>
            ) : null}
            <div className="border-t border-slate-100 pt-2.5">
            <div className="flex items-end justify-between gap-2">
              <div className="min-w-0 flex-1 text-right">
                {showOldPrice ? (
                  <p className="text-[11px] font-medium text-slate-400 line-through decoration-slate-400/80 decoration-1">
                    {offer.listShown.toLocaleString('fa-IR')} تومان
                  </p>
                ) : (
                  <p className="text-[11px] leading-none text-transparent">–</p>
                )}
                {offer.payShown > 0 ? (
                  <p className="mt-0.5 text-[14px] font-extrabold tabular-nums text-slate-900 sm:text-[15px]">
                    {offer.payShown.toLocaleString('fa-IR')} تومان
                  </p>
                ) : (
                  <div className="mt-0.5">
                    <FreeBadge size="sm" className="shadow-none" />
                  </div>
                )}
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-slate-50 px-2 py-1.5 text-[10px] font-bold text-[#3A68C2] ring-1 ring-slate-200/90 transition group-hover:bg-[#3A68C2]/8 group-hover:ring-[#3A68C2]/25 sm:text-[11px]">
                <span className="hidden sm:inline">جزئیات</span>
                <span className="sm:hidden">بیشتر</span>
                <ArrowLeft className="h-3.5 w-3.5 transition duration-200 group-hover:-translate-x-0.5" aria-hidden />
              </span>
            </div>
            </div>
          </div>
        </div>
      </motion.button>
    </li>
  );
}

function CourseCardListRow({
  course,
  index,
  randomFallback,
  navigate,
}: {
  course: Course;
  index: number;
  randomFallback: string;
  navigate: (path: string) => void;
}) {
  const thumb = course.thumbnail ? getImageUrlWithFallback(course.thumbnail, randomFallback) : randomFallback;
  const lessons = carouselLessonCount(course);
  const enroll = course._count?.enrollments ?? 0;
  const offer = browseCardOffer(course);
  const discountPct = offer.pct;
  const showPromoStripe = !!offer.endsAt && offer.payShown > 0 && discountPct > 0;
  const showOldPrice =
    Number.isFinite(offer.listShown) && offer.payShown > 0 && offer.listShown > offer.payShown;

  return (
    <motion.li
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.035, 0.25) }}
      className="list-none"
    >
      <button
        type="button"
        onClick={() => navigate(`/courses/${getCoursePathSegment(course)}`)}
        className="group flex w-full flex-col gap-2.5 rounded-xl border border-slate-200/95 bg-white px-3 py-3 text-start shadow-[0_1px_2px_rgba(15,23,42,0.04),0_6px_16px_-10px_rgba(15,23,42,0.08)] transition duration-300 hover:border-slate-300 hover:shadow-[0_14px_28px_-14px_rgba(15,23,42,0.12)] sm:flex-row sm:items-stretch sm:gap-3 sm:rounded-xl sm:p-3 md:gap-3 md:p-3.5"
      >
        {/* موبایل: تصویر + متن؛ sm+: همان ستون‌های افقی قبلی بدون ستون کم‌عرض فشرده */}
        <div className="flex min-w-0 flex-1 flex-row items-start gap-3 md:gap-4">
          <div className="relative aspect-[16/9] w-[10.5rem] shrink-0 overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200/60 sm:w-[11.5rem] md:w-[12rem]">
            <img
              src={thumb}
              alt=""
              className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
              loading={index < 6 ? 'eager' : 'lazy'}
              onError={(e) => {
                (e.target as HTMLImageElement).src = randomFallback;
              }}
            />
            {discountPct > 0 ? (
              <span className="absolute end-1 top-1 z-10 inline-flex rounded-md bg-[#dc2626] px-1 py-px text-[7px] font-extrabold leading-none text-white shadow-sm sm:hidden">
                ٪{discountPct.toLocaleString('fa-IR')}
              </span>
            ) : null}
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-1 sm:min-h-[4.5rem] sm:justify-between sm:gap-1.5 md:gap-2">
            <div>
              <h3 className="line-clamp-2 text-[13px] font-bold leading-snug text-[#0f172a] transition group-hover:text-[#1C3B9B] sm:text-sm sm:font-extrabold md:text-[0.9375rem]">
                {course.title}
              </h3>
              <p className="mt-1 truncate text-[11px] font-medium text-slate-500 sm:text-[11px] md:text-[12px]">
                {carouselInstructorLine(course)}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 gap-y-1 text-[10px] font-semibold text-slate-600 sm:mt-2 sm:text-[10px] md:gap-2 md:text-[11px]">
                <span className="inline-flex items-center gap-0.5 rounded-md bg-slate-50 px-1.5 py-0.5 text-slate-700 ring-1 ring-slate-200/80 sm:px-2 md:text-[11px]">
                  <Users className="h-2.5 w-2.5 shrink-0 text-[#1C3B9B] sm:h-3 sm:w-3" aria-hidden />
                  {enroll.toLocaleString('fa-IR')}
                  <span className="max-sm:hidden"> فراگیر</span>
                </span>
                <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-50/90 px-1.5 py-0.5 text-amber-900 ring-1 ring-amber-100/80 sm:px-2 md:text-[11px]">
                  <Star className="h-2.5 w-2.5 shrink-0 fill-amber-500 text-amber-500 sm:h-3 sm:w-3" aria-hidden />
                  {carouselRatingLine(course)}
                </span>
                {(course.introVideoAparatUrl || course.videoFile) && (
                  <span className="inline-flex items-center gap-0.5 rounded-md bg-[#eef2ff] px-1.5 py-0.5 font-bold text-[#1C3B9B] ring-1 ring-[#1C3B9B]/12 sm:px-2 md:text-[11px]">
                    <Award className="h-2.5 w-2.5 shrink-0 sm:h-3 sm:w-3" aria-hidden />
                    <span className="hidden min-[360px]:inline">معرفی</span>
                  </span>
                )}
                <span className="text-[10px] text-slate-400 md:text-[11px]">
                  {lessons.toLocaleString('fa-IR')} جلسه
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ستون قیمت: پیشنهاد/تایمر (خط کوتاه) هم‌مجموعه قیمت؛ موبایل ردیفی، دسکتاپ ستونی */}
        <div className="flex w-full shrink-0 flex-col gap-2 rounded-lg bg-slate-50/90 px-2.5 py-2.5 ring-1 ring-slate-200/70 sm:w-auto sm:min-w-[5.75rem] sm:justify-center sm:rounded-none sm:border-s sm:border-slate-200/85 sm:bg-transparent sm:px-0 sm:py-0 sm:ring-0 md:min-w-[6.25rem] md:ps-4">
          {showPromoStripe && offer.endsAt ? (
            <div className="flex w-full flex-col items-center sm:w-full">
              <div className="flex w-fit max-w-full flex-wrap items-center justify-center gap-2">
                <span className="shrink-0 text-[9px] font-semibold text-red-600 sm:text-[10px] md:text-[11px]">
                  پیشنهاد
                </span>
                <span className="inline-flex shrink-0 scale-95 [&_span]:!text-[10px] md:[&_span]:!text-[11px]">
                  <MinimalOfferCountdown endsAt={offer.endsAt} />
                </span>
              </div>
              <div className="mt-1.5 w-fit border-b border-red-200/90 sm:mt-2" aria-hidden />
            </div>
          ) : null}

          <div className="flex w-full flex-row items-center justify-between gap-3 sm:flex-col sm:justify-center sm:gap-2">
          {discountPct > 0 ? (
            <div
              className="mx-auto hidden size-11 shrink-0 flex-col items-center justify-center rounded-lg text-[8px] font-black leading-none text-white shadow-sm ring-[1.5px] ring-white sm:flex md:size-11 md:text-[9px]"
              style={{ backgroundImage: `linear-gradient(145deg, ${BRAND_BLUE}, ${BRAND_PINK})` }}
            >
              <span className="opacity-90">%</span>
              <span className="-mt-0.5 text-[10px] font-black tabular-nums md:text-[11px]">
                {discountPct.toLocaleString('fa-IR')}
              </span>
            </div>
          ) : (
            <div className="mx-auto hidden size-11 shrink-0 flex-col items-center justify-center rounded-lg bg-slate-100 text-[8px] font-bold leading-tight text-slate-400 sm:flex md:size-11">
              ویژه
            </div>
          )}

          <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-start sm:w-full sm:flex-none sm:items-center sm:text-center">
            {showOldPrice ? (
              <p className="text-[10px] font-medium tabular-nums text-slate-400 line-through decoration-red-400/70 md:text-[11px]">
                {offer.listShown.toLocaleString('fa-IR')}
              </p>
            ) : (
              <p className="text-[10px] leading-none text-transparent sm:text-[11px]">–</p>
            )}
            {offer.payShown > 0 ? (
              <p className="text-[15px] font-black tabular-nums leading-tight text-[#0f172a] sm:text-sm md:text-base lg:text-lg">
                {offer.payShown.toLocaleString('fa-IR')}
                <span className="ms-1 text-[10px] font-bold md:text-[11px]">تومان</span>
              </p>
            ) : (
              <div className="flex justify-start sm:justify-center">
                <FreeBadge size="sm" className="shadow-none" />
              </div>
            )}
          </div>

          <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[#3A68C2]/14 px-2.5 py-2 text-[10px] font-bold text-[#2855a3] ring-1 ring-[#3A68C2]/35 sm:hidden">
            جزئیات
            <ArrowLeft className="h-3 w-3 transition group-hover:-translate-x-0.5" />
          </span>
          <span className="hidden items-center justify-center gap-1 text-[10px] font-bold text-[#1C3B9B] sm:flex md:text-[11px]">
            جزئیات
            <ArrowLeft className="h-3 w-3 transition duration-200 group-hover:-translate-x-0.5" />
          </span>
          </div>
        </div>
      </button>
    </motion.li>
  );
}

const CourseBrowse: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSlug = searchParams.get('cat') ?? '';
  const headerSearchQ = searchParams.get('q');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [categories, setCategories] = useState<ContentCategoryPublic[]>([]);
  /** دستهٔ «معرف کتابخانه» با TinyMCE — فقط وقتی ?cat خالی است */
  const [browseLandingIntroCategory, setBrowseLandingIntroCategory] = useState<ContentCategoryPublic | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  /** هماهنگ با جستجوی هدر: `?q=` */
  useEffect(() => {
    if (headerSearchQ !== null) setSearchTerm(headerSearchQ);
  }, [headerSearchQ]);
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [onlyDiscount, setOnlyDiscount] = useState(false);
  const [onlyIntro, setOnlyIntro] = useState(false);
  const [onlyFree, setOnlyFree] = useState(false);
  const [ratingMin, setRatingMin] = useState('');
  const [durationBucket, setDurationBucket] = useState<DurationBucket>('');
  const [levelFilter, setLevelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [topicCategorySearch, setTopicCategorySearch] = useState('');

  const layoutMode = searchParams.get('view') === 'list' ? 'list' : 'grid';
  const setLayoutMode = useCallback(
    (mode: 'grid' | 'list') => {
      const next = new URLSearchParams(searchParams);
      if (mode === 'list') next.set('view', 'list');
      else next.delete('view');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /** زیر lg فقط ستونی؛ بدون انتخاب نمای شبکه‌ای */
  const [isDesktopBp, setIsDesktopBp] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsDesktopBp(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const displayMode = isDesktopBp ? layoutMode : 'list';

  const setCategorySlug = useCallback(
    (slug: string) => {
      const next = new URLSearchParams(searchParams);
      if (!slug) next.delete('cat');
      else next.set('cat', slug);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [cats, list] = await Promise.all([
          categoriesService.list('COURSE'),
          coursesService.getPublished(),
        ]);
        if (!alive) return;
        const activeCats = (cats as ContentCategoryPublic[])
          .filter((c) => c.isActive !== false)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        const navCats = activeCats.filter((c) => !c.browseLandingIntro);
        const landing =
          activeCats
            .filter((c) => c.browseLandingIntro && categoryRichHtmlIsNonEmpty(c.description))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))[0] ?? null;
        setCategories(navCats);
        setBrowseLandingIntroCategory(landing);
        setCourses(Array.isArray(list) ? list : []);
      } catch {
        if (!alive) return;
        setError('خطا در بارگذاری دسته‌ها یا دوره‌ها');
        setCategories([]);
        setBrowseLandingIntroCategory(null);
        setCourses([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (loading || !categories.length || !selectedSlug || selectedSlug === '_none') return;
    const valid = categories.some((c) => c.slug === selectedSlug);
    if (!valid) {
      const next = new URLSearchParams(searchParams);
      next.delete('cat');
      setSearchParams(next, { replace: true });
    }
  }, [loading, categories, selectedSlug, searchParams, setSearchParams]);

  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of courses) {
      const slug = c.category?.slug;
      if (!slug) continue;
      map.set(slug, (map.get(slug) ?? 0) + 1);
    }
    return map;
  }, [courses]);

  const uncategorizedCount = useMemo(() => courses.filter((c) => !c.category?.slug).length, [courses]);

  const levelOptions = useMemo(() => {
    const s = new Set<string>();
    for (const c of courses) {
      const v = c.courseLevel?.trim();
      if (v) s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'fa'));
  }, [courses]);

  const typeOptions = useMemo(() => {
    const s = new Set<string>();
    for (const c of courses) {
      const v = c.teachingFormat?.trim();
      if (v) s.add(v);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'fa'));
  }, [courses]);

  const filteredSorted = useMemo(() => {
    let list = [...courses];

    if (selectedSlug) {
      if (selectedSlug === '_none') list = list.filter((c) => !c.category?.slug);
      else list = list.filter((c) => c.category?.slug === selectedSlug);
    }

    const q = searchTerm.trim();
    if (q) {
      list = list.filter((c) => {
        const descBlob = `${htmlToPlainSnippet(c.description, 400)} ${htmlToPlainSnippet(c.longDescription ?? undefined, 400)}`;
        return (
          haystackIncludesFa(c.title, q) ||
          haystackIncludesFa(descBlob, q) ||
          haystackIncludesFa(c.category?.name ?? '', q)
        );
      });
    }

    if (onlyDiscount) list = list.filter((c) => c.hasActiveDiscount === true);
    if (onlyIntro) list = list.filter((c) => !!(c.introVideoAparatUrl || c.videoFile));
    if (onlyFree) list = list.filter((c) => payableTomans(c) === 0);

    if (ratingMin) {
      const min = Number(ratingMin);
      list = list.filter((c) => {
        const r = parsedCourseRating(c);
        if (r == null) return false;
        return r >= min;
      });
    }

    if (durationBucket) {
      list = list.filter((c) => matchesDurationBucket(c, durationBucket));
    }

    if (levelFilter) {
      list = list.filter((c) => (c.courseLevel || '').trim() === levelFilter);
    }

    if (typeFilter) {
      list = list.filter((c) => (c.teachingFormat || '').trim() === typeFilter);
    }

    list.sort((a, b) => {
      switch (sortKey) {
        case 'price-asc':
          return payableTomans(a) - payableTomans(b);
        case 'price-desc':
          return payableTomans(b) - payableTomans(a);
        case 'popular':
          return (b._count?.enrollments ?? 0) - (a._count?.enrollments ?? 0);
        case 'rating': {
          const ra = parsedCourseRating(a);
          const rb = parsedCourseRating(b);
          if (ra == null && rb == null) return 0;
          if (ra == null) return 1;
          if (rb == null) return -1;
          return rb - ra;
        }
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

    return list;
  }, [
    courses,
    selectedSlug,
    searchTerm,
    sortKey,
    onlyDiscount,
    onlyIntro,
    onlyFree,
    ratingMin,
    durationBucket,
    levelFilter,
    typeFilter,
  ]);

  const resetFilters = () => {
    setSearchTerm('');
    setSortKey('newest');
    setOnlyDiscount(false);
    setOnlyIntro(false);
    setOnlyFree(false);
    setRatingMin('');
    setDurationBucket('');
    setLevelFilter('');
    setTypeFilter('');
    setCategorySlug('');
    setTopicCategorySearch('');
    setMobileFiltersOpen(false);
  };

  const activeFilterCount =
    (selectedSlug ? 1 : 0) +
    (searchTerm.trim() ? 1 : 0) +
    (sortKey !== 'newest' ? 1 : 0) +
    (onlyDiscount ? 1 : 0) +
    (onlyIntro ? 1 : 0) +
    (onlyFree ? 1 : 0) +
    (ratingMin ? 1 : 0) +
    (durationBucket ? 1 : 0) +
    (levelFilter ? 1 : 0) +
    (typeFilter ? 1 : 0);

  const randomFallback = '/assets/homeV2/01.jpg';
  const selectedCategoryMeta = categories.find((c) => c.slug === selectedSlug);

  const pageTitle = selectedCategoryMeta?.name ?? 'همهٔ دوره‌های آموزشی';

  /** کنار عنوان: تصویر/رنگ دسته یا نماد عمومی؛ بند انگشتی از خود دسته یا از دورهٔ همان دسته (نه هر دورهٔ فیلترشده). */
  const browseToolbarBadge = useMemo(() => {
    if (selectedSlug === '_none') {
      return {
        kind: 'icon' as const,
        Icon: Layers,
        boxClasses: 'border border-amber-200/80 bg-amber-50 text-amber-700',
      };
    }
    if (selectedCategoryMeta && selectedSlug) {
      const fromCat = getImageUrl(selectedCategoryMeta.image);
      const fromCourse =
        courses.find((c) => c.category?.slug === selectedSlug && c.thumbnail)?.thumbnail;
      const fromFiltered =
        filteredSorted.filter((c) => c.category?.slug === selectedSlug).find((c) => c.thumbnail)
          ?.thumbnail;
      const rel = fromCourse ?? fromFiltered;
      const url = fromCat ?? (rel ? getImageUrl(rel) : null);
      if (url) return { kind: 'image' as const, url, alt: selectedCategoryMeta.name };
      const color =
        selectedCategoryMeta.color?.match(/^#[0-9a-fA-F]{3,8}$/i)?.[0] ?? COURSE_CTA_NAVY;
      return { kind: 'color' as const, color };
    }
    const anyThumb =
      filteredSorted.find((c) => c.thumbnail)?.thumbnail ?? courses.find((c) => c.thumbnail)?.thumbnail;
    const url = anyThumb ? getImageUrl(anyThumb) : null;
    if (url) return { kind: 'image' as const, url, alt: pageTitle };
    return {
      kind: 'icon' as const,
      Icon: LayoutGrid,
      boxClasses: 'border border-slate-200 bg-slate-50 text-slate-500',
    };
  }, [selectedSlug, selectedCategoryMeta, filteredSorted, courses, pageTitle]);

  const browseStats = useMemo(() => summarizeBrowseAudience(filteredSorted), [filteredSorted]);

  const bannerStripeAccent =
    selectedCategoryMeta?.color?.match(/^#[0-9a-fA-F]{3,8}$/i)?.[0] ?? COURSE_CTA_NAVY;

  const bannerMainHeading = useMemo(() => {
    if (selectedSlug === '_none') return 'دوره‌های بدون دسته‌بندی';
    if (selectedCategoryMeta?.name?.trim())
      return `دوره‌های ${selectedCategoryMeta.name.trim()}`;
    return 'کتابخانه دوره‌های آموزشی';
  }, [selectedSlug, selectedCategoryMeta]);

  const bannerSubtitle =
    selectedCategoryMeta?.shortDescription?.trim()?.slice(0, 220) ||
    (selectedSlug === '_none'
      ? 'دوره‌هایی که هنوز به یک دستهٔ موضوعی وصل نشده‌اند را اینجا ببینید.'
      : 'ویدئو، کارگاه و مسیر خودآموز — با نظارت آموزشی و دسترسی طولانی.');

  const crumbCurrent =
    selectedSlug === '_none' ? 'بدون دسته' : selectedCategoryMeta?.name ?? null;

  const filterPanelProps = {
    categories,
    courses,
    categoryCounts,
    uncategorizedCount,
    selectedSlug,
    setCategorySlug,
    searchTerm,
    setSearchTerm,
    onlyDiscount,
    setOnlyDiscount,
    onlyIntro,
    setOnlyIntro,
    onlyFree,
    setOnlyFree,
    resetFilters,
    activeFilterCount,
    ratingMin,
    setRatingMin,
    durationBucket,
    setDurationBucket,
    levelFilter,
    setLevelFilter,
    typeFilter,
    setTypeFilter,
    levelOptions,
    typeOptions,
    topicCategorySearch,
    setTopicCategorySearch,
  };

  return (
    <div
      className="min-h-screen pb-16 pt-4 text-[#0f172a]"
      style={{ backgroundColor: BROWSE_SURFACE }}
      dir="rtl"
    >
      <div className="relative z-10 mx-auto w-full max-w-[min(1520px,97vw)] px-3 sm:px-5 lg:px-6">
        {/* نان‌واژ + بنر هم‌تم با زمینه و کارت‌های صفحه */}
        <div className="mb-4">
          <nav
            aria-label="مسیر دسترسی"
            className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
          >
            <Link to="/" className="font-medium transition hover:text-[#3A68C2]">
              خانه
            </Link>
            <span className="text-slate-400 opacity-80" aria-hidden>
              ›
            </span>
            <Link to="/courses" className="font-medium transition hover:text-[#3A68C2]">
              کتابخانه دوره‌ها
            </Link>
            {crumbCurrent ? (
              <>
                <span className="text-slate-400 opacity-80" aria-hidden>
                  ›
                </span>
                <span className="max-w-[12rem] truncate font-semibold text-slate-700 sm:max-w-[20rem]">
                  {crumbCurrent}
                </span>
              </>
            ) : null}
          </nav>

          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            aria-labelledby="browse-hero-heading"
            className="relative mx-auto max-w-[min(1060px,100%)]"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-[20%] -start-2 z-0 w-1 rounded-full sm:inset-y-[14%] sm:-start-[0.875rem] sm:w-[3px]"
              style={{ backgroundColor: COURSE_CTA_NAVY }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-[20%] -end-2 z-0 w-1 rounded-full sm:inset-y-[14%] sm:-end-[0.875rem] sm:w-[3px]"
              style={{ backgroundColor: COURSE_CTA_NAVY }}
            />

            <div className="relative z-[1] overflow-hidden rounded-2xl border border-slate-200/85 bg-white shadow-[0_2px_28px_-14px_rgba(58,104,194,0.18),0_8px_32px_-24px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/55 sm:rounded-[1.25rem]">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3A68C2]/55 to-transparent opacity-95" aria-hidden />
              <div
                className="pointer-events-none absolute -start-24 -top-20 h-48 w-48 rounded-full opacity-[0.22]"
                style={{ backgroundColor: bannerStripeAccent }}
                aria-hidden
              />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#fafbff] via-white to-[#f0f5fc]"
                aria-hidden
              />

              <div className="relative px-4 py-4 sm:px-6 sm:py-5">
                <h1
                  id="browse-hero-heading"
                  className="text-center text-[1.0625rem] font-extrabold leading-tight tracking-tight text-[#0f172a] sm:text-xl md:text-2xl"
                >
                  {bannerMainHeading}
                </h1>
                <p className="mx-auto mt-1.5 max-w-2xl line-clamp-2 text-center text-[11px] leading-snug text-slate-600 sm:mt-2 sm:text-xs">
                  {bannerSubtitle}
                </p>

                {!selectedSlug &&
                  browseLandingIntroCategory &&
                  categoryRichHtmlIsNonEmpty(browseLandingIntroCategory.description) && (
                    <div className="mx-auto mt-4 w-full max-w-3xl border-t border-slate-200/85 pt-4 text-start sm:mt-5 sm:pt-5">
                      <p className="mb-2 text-center text-[11px] font-semibold text-[#3A68C2] sm:text-xs">
                        {browseLandingIntroCategory.name?.trim() || 'دربارهٔ کتابخانهٔ دوره‌ها'}
                      </p>
                      <BrowseCategoryRichCollapsible
                        html={browseLandingIntroCategory.description!}
                        segmentKey={String(browseLandingIntroCategory.id)}
                        loading={loading}
                      />
                    </div>
                  )}

                {!loading ? (
                  (() => {
                    const tertiaryHours = browseStats.mediaHours >= 0.5;
                    const tertiaryIcon = tertiaryHours ? Clock : ClipboardList;
                    const tertiaryLabel = tertiaryHours ? 'ساعت ویدئو و صوت' : 'جلسه آموزشی';
                    const tertiaryValue = tertiaryHours
                      ? browseStats.mediaHours.toLocaleString('fa-IR', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: browseStats.mediaHours % 1 ? 1 : 0,
                        })
                      : browseStats.lessonSum.toLocaleString('fa-IR');

                    const blocks: { value: string; Ico: LucideIcon; sub: string }[] = [
                      {
                        value: browseStats.enrollSum.toLocaleString('fa-IR'),
                        Ico: Users,
                        sub: 'فراگیر',
                      },
                      {
                        value: filteredSorted.length.toLocaleString('fa-IR'),
                        Ico: BookOpen,
                        sub: 'دوره',
                      },
                      {
                        value: tertiaryValue,
                        Ico: tertiaryIcon,
                        sub: tertiaryLabel,
                      },
                    ];

                    return (
                      <div className="mx-auto mt-3.5 flex max-w-xl flex-wrap items-center justify-center gap-x-7 gap-y-2.5 border-t border-slate-100 pt-3 sm:mt-4 sm:max-w-3xl sm:gap-x-10 sm:gap-y-0 sm:pt-3.5 md:gap-x-12">
                        {blocks.map((b, i) => (
                          <div key={i} className="flex shrink-0 items-center gap-2">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#3A68C2]/11 ring-1 ring-[#3A68C2]/25">
                              <b.Ico className="h-4 w-4 text-[#2f5599]" aria-hidden strokeWidth={2.1} />
                            </div>
                            <div className="min-w-0 text-start">
                              <p className="flex items-baseline gap-px text-base font-bold tabular-nums text-[#0f172a] md:text-[1.0625rem]">
                                <span aria-hidden className="text-[11px] font-semibold text-[#3A68C2]">
                                  +
                                </span>
                                {b.value}
                              </p>
                              <p className="text-[10px] font-medium leading-tight text-slate-500">{b.sub}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <div className="mx-auto mt-4 flex max-w-xl justify-center gap-2 border-t border-slate-100 pt-3.5">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="h-10 flex-1 max-w-[6.5rem] animate-pulse rounded-lg bg-slate-100" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.section>
        </div>

        {/* در RTL ستون اول = سمت شروع؛ فیلترها؛ sticky بر اساس ارتفاع واقعی هدر (با/بدون بنر) */}
        <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-5">
          <aside className="hidden lg:z-40 lg:block lg:w-[264px] lg:shrink-0 lg:self-start lg:sticky lg:top-[calc(var(--site-header-height,96px)+12px)] xl:w-[288px]">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between px-0.5 py-2">
                <h2 className="flex items-center gap-2 text-[13px] font-semibold tracking-tight text-slate-800">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-200/70">
                    <Settings2 className="h-4 w-4" aria-hidden />
                  </span>
                  فیلترها
                </h2>
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-slate-600 ring-1 ring-slate-200/60">
                    {activeFilterCount.toLocaleString('fa-IR')}
                  </span>
                ) : null}
              </div>
              <div className="max-h-[calc(100dvh-var(--site-header-height,96px)-28px-env(safe-area-inset-top,0px))] overflow-y-auto pb-2 pt-0 [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]">
                <FilterPanelContent {...filterPanelProps} />
              </div>
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {/* نوار عنوان + مرتب‌سازی + سوئیچ نما — شبیه تولبار لیست دورهٔ مرجع */}
            <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                <div className="min-w-0 flex-1 border-s-[2px] border-slate-200 ps-3">
                  <div className="flex items-start gap-3">
                    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-inner sm:h-[4.25rem] sm:w-[4.25rem]">
                      {browseToolbarBadge.kind === 'image' ? (
                        <img
                          src={browseToolbarBadge.url}
                          alt={browseToolbarBadge.alt}
                          className="h-full w-full object-cover"
                        />
                      ) : browseToolbarBadge.kind === 'color' ? (
                        <div
                          className="h-full w-full"
                          style={{ backgroundColor: browseToolbarBadge.color }}
                          aria-hidden
                        />
                      ) : (
                        <div
                          className={`flex h-full w-full items-center justify-center ${browseToolbarBadge.boxClasses}`}
                          aria-hidden
                        >
                          {(() => {
                            const BadgeIcon = browseToolbarBadge.Icon;
                            return (
                              <BadgeIcon className="h-8 w-8 shrink-0 opacity-95" strokeWidth={1.75} />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <h2 className="text-base font-semibold text-[#0f172a] sm:text-lg">{pageTitle}</h2>
                      {selectedCategoryMeta?.shortDescription ? (
                        <p className="mt-1 line-clamp-2 max-w-2xl text-xs text-slate-600">
                          {selectedCategoryMeta.shortDescription}
                        </p>
                      ) : (
                        <p className="mt-1 max-w-2xl text-xs text-slate-500">
                          هر بلوک فیلتر را باز کنید (کلیک روی عنوان).
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap xl:justify-end">
                  <div className="hidden shrink-0 gap-0.5 rounded-md border border-slate-200 bg-slate-50 p-0.5 lg:inline-flex">
                    <button
                      type="button"
                      onClick={() => setLayoutMode('grid')}
                      aria-pressed={layoutMode === 'grid'}
                      aria-label="نمای کارت"
                      className={`rounded-[4px] p-2 transition ${layoutMode === 'grid' ? 'bg-[#3A68C2]/12 text-[#2855a3] ring-1 ring-[#3A68C2]/35' : 'text-slate-500 hover:text-[#0f172a]'}`}
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setLayoutMode('list')}
                      aria-pressed={layoutMode === 'list'}
                      aria-label="نمای ستونی و ردیفی"
                      className={`rounded-[4px] p-2 transition ${layoutMode === 'list' ? 'bg-[#3A68C2]/12 text-[#2855a3] ring-1 ring-[#3A68C2]/35' : 'text-slate-500 hover:text-[#0f172a]'}`}
                    >
                      <Rows3 className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen(true)}
                    className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 sm:text-[13px] lg:hidden"
                  >
                    <Filter className="h-4 w-4" />
                    فیلترها
                    {activeFilterCount > 0 ? (
                      <span className="rounded-md bg-slate-100 px-2 text-[11px] tabular-nums text-slate-700">
                        {activeFilterCount.toLocaleString('fa-IR')}
                      </span>
                    ) : null}
                  </button>
                </div>
              </div>
              <div className="mt-3 border-t border-slate-100 pt-3">
                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  مرتب‌سازی
                </p>
                <ToolbarSortPills sortKey={sortKey} setSortKey={setSortKey} />
              </div>
            </div>

            {loading && (
              <>
                <div
                  className={`${displayMode === 'grid' ? 'grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:gap-4' : 'flex flex-col gap-2 lg:gap-2.5'} `}
                >
                  {Array.from({ length: displayMode === 'grid' ? 6 : 5 }).map((_, i) => (
                    <div
                      key={i}
                      className={`animate-pulse overflow-hidden rounded-xl border border-slate-200 bg-white ${
                        displayMode === 'list'
                          ? 'flex min-h-0 flex-col gap-2.5 px-3 py-3 sm:flex-row sm:items-stretch sm:gap-3 sm:p-3'
                          : 'flex flex-col'
                      }`}
                    >
                      {displayMode === 'list' ? (
                        <>
                          <div className="flex min-w-0 flex-1 flex-row items-start gap-3">
                            <div className="aspect-[16/9] w-[10.5rem] shrink-0 rounded-xl bg-slate-200 ring-1 ring-slate-200/45 sm:w-[11.5rem] md:w-[12rem]" />
                            <div className="flex flex-1 flex-col justify-center gap-2">
                              <div className="h-6 w-[78%] rounded-md bg-slate-200" />
                              <div className="h-3 w-2/5 rounded bg-slate-100" />
                            </div>
                          </div>
                          <div className="flex h-12 w-full flex-row items-center gap-3 rounded-lg bg-slate-50 px-2 ring-1 ring-slate-100 sm:hidden">
                            <div className="min-w-0 flex-1 space-y-1.5 py-1 ps-1">
                              <div className="h-2.5 w-1/3 rounded bg-slate-200" />
                              <div className="h-4 w-3/4 rounded-md bg-slate-200" />
                            </div>
                            <div className="h-9 w-14 shrink-0 rounded-lg bg-slate-200" />
                          </div>
                          <div className="hidden h-full min-w-[5.5rem] shrink-0 flex-col items-center justify-center gap-2 border-s border-slate-100 ps-4 sm:flex md:min-w-[6rem]">
                            <div className="size-11 rounded-lg bg-slate-200" />
                            <div className="h-3 w-14 rounded bg-slate-200" />
                            <div className="h-4 w-full max-w-[5rem] rounded-md bg-slate-200" />
                            <div className="h-3 w-16 rounded bg-slate-100" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="aspect-[16/9] w-full shrink-0 rounded-none bg-slate-200 ring-0" />
                          <div className="flex flex-1 flex-col gap-1.5 p-3">
                            <div className="h-5 w-2/3 rounded bg-slate-200" />
                            <div className="h-4 w-full rounded bg-slate-100" />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {error && !loading && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-10 text-center">
                <p className="font-bold text-red-800">{error}</p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-xl bg-red-600 px-6 py-2.5 text-sm font-bold text-white"
                >
                  تلاش مجدد
                </button>
              </div>
            )}

            <AnimatePresence mode="popLayout">
              {!loading &&
                !error &&
                filteredSorted.length > 0 &&
                (displayMode === 'grid' ? (
                  <motion.ul
                    key="view-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:gap-4"
                  >
                    {filteredSorted.map((course, index) => (
                      <CourseCardGrid
                        key={course.id}
                        course={course}
                        index={index}
                        randomFallback={randomFallback}
                        navigate={navigate}
                      />
                    ))}
                  </motion.ul>
                ) : (
                  <motion.ul
                    key="view-list"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col gap-2 lg:gap-2.5"
                  >
                    {filteredSorted.map((course, index) => (
                      <CourseCardListRow
                        key={course.id}
                        course={course}
                        index={index}
                        randomFallback={randomFallback}
                        navigate={navigate}
                      />
                    ))}
                  </motion.ul>
                ))}
            </AnimatePresence>

            {!loading && !error && filteredSorted.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-5 py-16 text-center shadow-sm">
                <List className="mx-auto mb-4 h-10 w-10 text-slate-300" aria-hidden />
                <p className="text-xl font-black text-[#0f172a]">موردی مطابق این فیلتر نیست</p>
                <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
                  بازهٔ امتیاز، مدت دوره یا سطح را بازتر کنید.
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-8 rounded-md bg-[#3A68C2] px-10 py-2.5 text-sm font-semibold text-white transition hover:bg-[#345DAF]"
                >
                  حذف همهٔ فیلترها
                </button>
              </div>
            )}
          </div>
        </div>

        {selectedSlug &&
          selectedSlug !== '_none' &&
          selectedCategoryMeta &&
          categoryRichHtmlIsNonEmpty(selectedCategoryMeta.description) && (
            <section
              className="mt-6 sm:mt-8"
              aria-label={
                selectedCategoryMeta.name?.trim()
                  ? `توضیحات ${selectedCategoryMeta.name.trim()}`
                  : 'توضیحات دستهٔ انتخاب‌شده'
              }
            >
              <div
                className="rounded-2xl px-4 py-5 ring-1 ring-slate-200/40 sm:px-7 sm:py-7"
                style={{ backgroundColor: BROWSE_SURFACE }}
              >
                <div className="mb-4 flex items-center gap-2 sm:mb-5">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: bannerStripeAccent }}
                    aria-hidden
                  />
                  <h2 className="text-base font-extrabold text-[#0f172a] sm:text-xl">
                    دربارهٔ {selectedCategoryMeta.name?.trim() || 'این دسته'}
                  </h2>
                </div>
                <BrowseCategoryRichCollapsible
                  html={selectedCategoryMeta.description!}
                  segmentKey={String(selectedCategoryMeta.id)}
                  loading={loading}
                  embed
                />
              </div>
            </section>
          )}
      </div>

      <AnimatePresence>
        {mobileFiltersOpen && (
          <>
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              aria-label="بستن"
              className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-[2px] lg:hidden"
              onClick={() => setMobileFiltersOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed top-0 right-0 z-[101] flex h-[100dvh] w-[min(100vw,24.5rem)] flex-col overflow-hidden border-e border-slate-200/90 bg-white shadow-[0_0_40px_-10px_rgba(15,23,42,0.25)] lg:hidden"
              dir="rtl"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-[6px]">
                <span className="text-sm font-bold text-slate-800">فیلترها</span>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"
                  aria-label="بستن"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-4">
                <FilterPanelContent {...filterPanelProps} />
              </div>
              <div className="shrink-0 border-t border-slate-100 bg-slate-50/80 p-4 backdrop-blur-[4px]">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full rounded-md bg-[#3A68C2] py-2.5 text-sm font-semibold text-white hover:bg-[#345DAF]"
                >
                  نمایش {filteredSorted.length.toLocaleString('fa-IR')} دوره
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CourseBrowse;
